import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { IoIosArrowDropleft } from 'react-icons/io';
import { SubmitButton } from '../../../pages/common/forms/SubmitButton';
import { createMeetingSimple } from '../../../../app/page/page-storage/meetings-simple/meetings-simple.actions';
import { MeetingSimpleType } from '../../../../app/resource/meeting-simple/meeting-simple.types';
import { useSelect } from '../../../../utils/selector.utils';
import { authSelectors } from '../../../../app/auth/auth.selectors';
import Step1 from './Steps/Step1';
import Step2 from './Steps/Step2';
import Step4 from './Steps/Step4';
import Step3 from './Steps/Step3';
import { IAgendaItem } from '../../../../app/resource/agenda-item/agenda-item.types';
import { IUser } from '../../../../app/resource/user/user.types';

interface IFormData {
  operation: 'clone' | 'create';
  sourceMeetingId: string;
  title: string;
  sharepointUrl: string;
  interval: string;
  repeatType: string;
  repeatEvery: number;
  startDate: string;
  startTime: string;
  type: MeetingSimpleType;
  agendaItems: IAgendaItem[];
  administrators: IUser['id'][];
  members: IUser['id'][];
}

const MeetingTemplatesForm: FunctionComponent<{ onClose: () => void }> = ({ onClose }) => {
  const loggedUserId = useSelect(authSelectors.selectLoggedUser) || '';
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [areUsersSelected, setAreUsersSelected] = useState(false);
  const [formData, setFormData] = useState<IFormData>({
    operation: 'create',
    sourceMeetingId: '',
    title: '',
    sharepointUrl: '',
    interval: '',
    repeatType: '',
    repeatEvery: 0,
    startDate: '',
    startTime: '',
    type: 'meeting',
    agendaItems: [],
    administrators: [],
    members: [],
  });

  const next = useCallback((data: any) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setCurrentStep((s) => s + 1);
  }, []);

  const handleSubmit = () => {
    const formDataWithCorrectType = {
      title: formData.title,
      sharepointUrl: formData.sharepointUrl,
      operation: formData.operation,
      repeatType: formData.repeatType,
      repeatEvery: formData.repeatEvery,
      startDate: formData.startDate,
      startTime: formData.startTime,
      type: formData.type as MeetingSimpleType,
      sourceMeetingId: formData.sourceMeetingId,
      administrators: formData.administrators.length > 0 ? formData.administrators : [loggedUserId],
      members: formData.members,
    };

    dispatch(createMeetingSimple(formDataWithCorrectType));
  };

  const getButtonText = () => {
    if (currentStep === 2) {
      if (!areUsersSelected) {
        return 'Skip';
      }
    } else if (currentStep === 3) {
      return 'Finish';
    }
    return 'Next';
  };

  return (
    <div className={`new-meeting-form ${currentStep === 0 ? 'w700' : 'w500'}`}>
      <div className="panel-header small-header flex jcsb">
        <h3 className="f-16">
          {currentStep === 0 && 'Step One: Build Your Recurring Meeting Agenda'}
          {currentStep === 1 && 'Step Two: Meeting Details'}
          {currentStep === 2 && 'Step Three: Add Users'}
          {currentStep === 3 && 'Step Four: Confirm Details'}
        </h3>
      </div>
      <div className="panel-body pt-0">
        {currentStep === 0 && <Step1 onNext={next} />}
        {currentStep === 1 && <Step2 onNext={next} data={formData} />}
        {currentStep === 2 && <Step3 onNext={next} data={formData} setAreUsersSelected={setAreUsersSelected} />}
        {currentStep === 3 && <Step4 defaultValues={formData} onSubmit={handleSubmit} />}
      </div>
      <div className="panel-footer flex justify-end jcr relative">
        <div className="align-center flex" style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          {currentStep !== 0 && (
            <IoIosArrowDropleft size={22} onClick={() => setCurrentStep(currentStep - 1)} className="pointer" />
          )}
          <span className="mx-1">{currentStep + 1} of 4</span>
        </div>
        <a className="link blueGray mr-2 close" onClick={onClose}>
          Close
        </a>
        {currentStep !== 0 && (
          <SubmitButton apiKey="createMeetingFromApi" formName="meeting-templates-form" text={getButtonText()} />
        )}
      </div>
    </div>
  );
};

export default MeetingTemplatesForm;
